import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Calendar, Shield, CreditCard, Edit, Lock, Loader2, User, Crown, Clock, Check, X, Send, Building2, GraduationCap, ArrowUpRight, Laptop } from "lucide-react";
import { Input } from "../ui/input";
import { EditProfile } from "./EditProfile";
import { EditPassword } from "./EditPassword";
import { useProfile } from "../../contexts/ProfileContext";
import { api } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import { getAvatarUrl } from "../../utils/avatar";
import { HOME_LINKS } from "../../config/homeLinks";

const getSupabaseUrl = () => {
  return (import.meta as any).env?.VITE_SUPABASE_URL || 'https://bandarmolony.supabase.co';
};
const logoUrl = `${getSupabaseUrl()}/storage/v1/object/public/assets/images/logo_bandarmolony_v2.png`;

export function ProfilePage() {
  const navigate = useNavigate();
  const { profile, isLoading, updateProfile } = useProfile();
  const { showToast } = useToast();
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isEditPasswordOpen, setIsEditPasswordOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);

  const [isEditingTelegram, setIsEditingTelegram] = useState(false);
  const [telegramIdInput, setTelegramIdInput] = useState("");
  const [isSavingContact, setIsSavingContact] = useState(false);

  const [isEditingWhatsapp, setIsEditingWhatsapp] = useState(false);
  const [whatsappInput, setWhatsappInput] = useState("");

  useEffect(() => {
    loadSubscriptionStatus();
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      setIsSubscriptionLoading(true);
      const response = await api.getSubscriptionStatus();
      if (response.success) {
        setSubscriptionStatus(response.data);
      }
    } catch (error) {
      console.error('Failed to load subscription status:', error);
    } finally {
      setIsSubscriptionLoading(false);
    }
  };

  const getSubscriptionInfo = () => {
    // Admin and Developer have Pro plan forever (no end date)
    if (profile?.role === 'admin' || profile?.role === 'developer') {
      return [{
        status: 'active',
        daysLeft: 0,
        isActive: true,
        planName: 'Pro Plan',
        endDate: undefined,
        isLifetime: true // Flag to indicate lifetime subscription
      }];
    }

    if (!subscriptionStatus?.active_subscriptions || subscriptionStatus.active_subscriptions.length === 0) {
      if (!subscriptionStatus?.subscription) {
        return [{ status: 'inactive', daysLeft: 0, isActive: false, planName: 'Free Plan', endDate: undefined, isLifetime: false }];
      }
    }

    const subs = subscriptionStatus?.active_subscriptions && subscriptionStatus.active_subscriptions.length > 0
      ? subscriptionStatus.active_subscriptions
      : (subscriptionStatus?.subscription ? [subscriptionStatus.subscription] : []);

    if (subs.length === 0) {
      return [{ status: 'inactive', daysLeft: 0, isActive: false, planName: 'Free Plan', endDate: undefined, isLifetime: false }];
    }

    const mappedSubs = subs.map((subscription: any) => {
      // Calculate actual days left first
      const now = new Date();
      const endField = subscription.free_trial_end_date || subscription.end_date;
      const endDate = endField ? new Date(endField) : new Date(0);
      const actualDaysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      // If it's failed or really has no end date, ignore it
      if (subscription.status === 'failed' || !endField) {
        return { status: 'failed', daysLeft: 0, actualDaysLeft: 0, isActive: false, planName: 'Failed', endDate: undefined, isLifetime: false };
      }

      // If it truly has expired (time is up)
      if (actualDaysLeft <= 0) {
        return {
          status: 'expired',
          daysLeft: 0,
          actualDaysLeft: 0,
          isActive: false,
          planName: subscription.plan_name || 'Free Plan',
          endDate: subscription.end_date,
          isLifetime: false
        };
      }

      // It has time left! Even if the DB status is 'expired' or 'cancelled'
      let planName = subscription.plan_name;
      if (subscription.status === 'trial') planName += ' (Trial)';

      // If DB marked it as expired but it still has time, visually treat it as cancelled so it renders
      let visualStatus = subscription.status;
      if (visualStatus === 'expired') visualStatus = 'cancelled';

      const isActive = ['active', 'cancelled', 'trial'].includes(visualStatus);

      const visualDaysLeft = subscription.plan_duration
        ? Math.min(actualDaysLeft, subscription.plan_duration)
        : actualDaysLeft;

      return {
        status: visualStatus,
        daysLeft: Math.max(0, visualDaysLeft),
        actualDaysLeft: Math.max(0, actualDaysLeft),
        isActive,
        planName,
        endDate: subscription.end_date,
        isLifetime: false
      };
    });

    const validSubs = mappedSubs.filter((sub: any) => {
      if (sub.status === 'failed') return false;
      if (sub.actualDaysLeft <= 0) return false;
      return true;
    });

    if (validSubs.length === 0) {
      return [{ status: 'inactive', daysLeft: 0, actualDaysLeft: 0, isActive: false, planName: 'Free Plan', endDate: undefined, isLifetime: false }];
    }

    return validSubs;
  };

  const formatDuration = (days: number) => {
    if (days >= 30) {
      const months = Math.round(days / 30);
      return `${months} month${months !== 1 ? 's' : ''}`;
    }
    return `${days} day${days !== 1 ? 's' : ''}`;
  };

  const handleUpdateProfile = async (data: any) => {
    try {
      console.log('ProfilePage: Updating profile with data:', data);
      console.log('ProfilePage: Data types:', {
        full_name: typeof data.full_name,
        avatar_url: typeof data.avatar_url,
        name: typeof data.name,
        avatarUrl: typeof data.avatarUrl
      });

      // Update profile via API
      const updatedProfile = await api.updateProfile(data);

      // Update profile in context for real-time UI update
      updateProfile(updatedProfile);

      showToast({
        type: 'success',
        title: 'Profile Berhasil Diupdate!',
        message: 'Perubahan telah disimpan.',
      });

      console.log('✅ ProfilePage: Profile updated successfully');
    } catch (error) {
      console.error('ProfilePage: Failed to update profile:', error);
      throw error;
    }
  };

  const handleSaveContact = async (type: 'telegram' | 'whatsapp') => {
    try {
      setIsSavingContact(true);
      const data: any = {};
      if (type === 'telegram') {
        data.telegram_id = telegramIdInput;
      } else {
        data.whatsapp_number = whatsappInput;
      }

      const updatedProfile = await api.updateProfile(data);
      updateProfile(updatedProfile);

      showToast({
        type: 'success',
        title: 'Berhasil Diupdate!',
        message: type === 'telegram' ? 'Telegram ID telah disimpan.' : 'Nomor WhatsApp telah disimpan.',
      });

      if (type === 'telegram') setIsEditingTelegram(false);
      if (type === 'whatsapp') setIsEditingWhatsapp(false);
    } catch (error) {
      console.error(`Failed to update ${type}:`, error);
      showToast({
        type: 'error',
        title: 'Gagal Update',
        message: 'Terjadi kesalahan saat menyimpan data.',
      });
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleChangePassword = async (data: { currentPassword: string; newPassword: string }) => {
    try {
      console.log('ProfilePage: Starting password change...');

      if (!profile?.email) {
        throw new Error('Profile email not found');
      }

      // Use proper API endpoint with current password verification
      const result = await api.changePassword(data.currentPassword, data.newPassword);

      if (!result.success) {
        throw new Error(result.message || 'Failed to change password');
      }

      console.log('✅ ProfilePage: Password changed successfully');

      // Show success toast
      showToast({
        type: 'success',
        title: 'Password Berhasil Diubah!',
        message: 'Silahkan login kembali dengan password baru.',
      });

      // Close modal ONLY on success
      setIsEditPasswordOpen(false);

      // No need to refresh profile - session already refreshed in API call
    } catch (error: any) {
      console.error('❌ ProfilePage: Failed to change password:', error);

      // Re-throw error to let EditPassword component handle it
      throw error;
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="ml-2 text-muted-foreground">Loading profile…</span>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center py-16">
          <p className="text-muted-foreground mb-4">Please log in to view your profile</p>
          <Button onClick={() => window.location.href = '/auth'}>Go to Login</Button>
        </div>
      </div>
    );
  }

  const isActive = profile.subscriptionStatus === "active";

  return (
    <div className="h-full w-full overflow-hidden">
      <div className="h-full flex flex-col w-full px-2 sm:px-3">
        <div className="flex flex-col gap-2 py-1 sm:py-2">

          {/* Top Row: Left + Right columns */}
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-2">
            {/* Left Column */}
            <div className="w-full lg:w-1/2 flex flex-col gap-2">
              <Card>
                <CardContent className="pt-4 pb-3 px-4 sm:px-5">
                  <div className="flex flex-col items-center">
                    <Avatar className="w-40 h-40 sm:w-44 sm:h-44 ring-4 ring-background shadow-md rounded-full">
                      <AvatarImage
                        src={getAvatarUrl(profile.avatar_url) || getAvatarUrl(profile.avatarUrl) || getAvatarUrl(profile.avatar) || undefined}
                        alt={profile.full_name || profile.name}
                      />
                      <AvatarFallback className="bg-muted text-muted-foreground">
                        <User className="w-16 h-16 sm:w-18 sm:h-18" />
                      </AvatarFallback>
                    </Avatar>
                    <h2 className="mt-2 text-lg sm:text-xl font-semibold text-center px-2 break-words">Hi, {profile.full_name || profile.name}</h2>
                    <div className="mt-2 w-full max-w-sm flex flex-col sm:flex-row gap-2">
                      <Button variant="outline" onClick={() => setIsEditProfileOpen(true)} className="w-full sm:flex-1" size="sm">
                        <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit Profile
                      </Button>
                      <Button variant="outline" onClick={() => setIsEditPasswordOpen(true)} className="w-full sm:flex-1" size="sm">
                        <Lock className="w-3.5 h-3.5 mr-1.5" /> Change Password
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-3 gap-2 mt-auto">
                <Card className="flex flex-col">
                  <CardContent className="p-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-muted-foreground">Member Since</div>
                      <div className="text-sm font-medium break-words">{profile.joinedDate}</div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="flex flex-col">
                  <CardContent className="p-2 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-muted-foreground">Current Plan</div>
                      <div className="text-sm font-medium break-words">
                        {(() => {
                          if (profile?.role === 'admin' || profile?.role === 'developer') return "Pro";
                          if (isSubscriptionLoading) return <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />;
                          const infos = getSubscriptionInfo();
                          const activeInfos = infos.filter((i: any) => i.isActive);
                          if (activeInfos.length > 0) return activeInfos.map((i: any) => i.planName.replace(' (Trial)', '')).join(' + ');
                          return "-";
                        })()}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="flex flex-col">
                  <CardContent className="p-2 flex items-center gap-2">
                    <Shield className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-muted-foreground">Account Status</div>
                      <div className="text-sm font-medium break-words">
                        {profile.is_active ? "Active" : "Inactive"} • {profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Right Column */}
            <div className="w-full lg:w-1/2 flex flex-col">
              <Card>
                <CardHeader className="py-2 px-4">
                  <CardTitle className="text-base">Account Details</CardTitle>
                </CardHeader>
                <CardContent className="px-4 sm:px-5 py-2">
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <div className="min-w-full px-4 sm:px-0">
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b border-border/60">
                            <td className="py-1 text-muted-foreground min-w-[120px] sm:w-48">Full Name</td>
                            <td className="py-1 break-words">{profile.full_name || profile.name}</td>
                          </tr>
                          <tr className="border-b border-border/60">
                            <td className="py-1 text-muted-foreground min-w-[120px] sm:w-48">Email</td>
                            <td className="py-1">
                              <div className="flex items-center gap-2 break-all">
                                {profile.email}
                                <Badge variant={profile.email_verified ? "default" : "secondary"} className="text-[10px] px-1.5 py-0 h-4 shrink-0">
                                  {profile.email_verified ? "Verified" : "Not Verified"}
                                </Badge>
                              </div>
                            </td>
                          </tr>
                          <tr className="border-b border-border/60">
                            <td className="py-1 text-muted-foreground min-w-[120px] sm:w-48">Role</td>
                            <td className="py-1">
                              <Badge variant={profile.role === 'admin' ? "default" : "outline"}>
                                {profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
                              </Badge>
                            </td>
                          </tr>
                          <tr className="border-b border-border/60">
                            <td className="py-2 text-muted-foreground min-w-[120px] sm:w-48 align-middle">WhatsApp Number</td>
                            <td className="py-2">
                              {isEditingWhatsapp ? (
                                <div className="flex items-center gap-2">
                                  <Input value={whatsappInput} onChange={(e) => setWhatsappInput(e.target.value)} placeholder="+6281234567890" className="h-8 w-[200px]" />
                                  <Button size="icon" className="h-8 w-8" onClick={() => handleSaveContact('whatsapp')} disabled={isSavingContact}>
                                    {isSavingContact ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={() => setIsEditingWhatsapp(false)} disabled={isSavingContact}>
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-3">
                                  {profile.whatsapp_number ? (
                                    <span className="break-words font-medium">{profile.whatsapp_number}</span>
                                  ) : (
                                    <span className="text-muted-foreground italic text-sm">Belum diatur</span>
                                  )}
                                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => { setWhatsappInput(profile.whatsapp_number || ''); setIsEditingWhatsapp(true); }}>
                                    <Edit className="h-3 w-3 mr-1" /> Edit
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                          {(isActive || profile.role === 'admin' || profile.role === 'developer') && (
                            <tr className="border-b border-border/60">
                              <td className="py-2 text-muted-foreground min-w-[120px] sm:w-48 align-middle">Telegram ID</td>
                              <td className="py-2">
                                {isEditingTelegram ? (
                                  <div className="flex items-center gap-2">
                                    <Input value={telegramIdInput} onChange={(e) => setTelegramIdInput(e.target.value)} placeholder="@username" className="h-8 w-[200px]" />
                                    <Button size="icon" className="h-8 w-8" onClick={() => handleSaveContact('telegram')} disabled={isSavingContact}>
                                      {isSavingContact ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                    </Button>
                                    <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={() => setIsEditingTelegram(false)} disabled={isSavingContact}>
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-3">
                                    {profile.telegram_id ? (
                                      <span className="break-words font-medium">{profile.telegram_id}</span>
                                    ) : (
                                      <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); handleSaveContact('telegram'); }}>
                                        <Input value={telegramIdInput} onChange={(e) => setTelegramIdInput(e.target.value)} placeholder="Masukkan Telegram ID (mis: @username)" className="h-8 w-[240px]" />
                                        <Button type="submit" size="sm" className="h-8" disabled={isSavingContact || !telegramIdInput}>
                                          {isSavingContact ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />} Simpan
                                        </Button>
                                      </form>
                                    )}
                                    {profile.telegram_id && (
                                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => { setTelegramIdInput(profile.telegram_id || ''); setIsEditingTelegram(true); }}>
                                        <Edit className="h-3 w-3 mr-1" /> Edit
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                          <tr className="border-b border-border/60">
                            <td className="py-1 text-muted-foreground min-w-[120px] sm:w-48">Joined Since</td>
                            <td className="py-1 break-words">{profile.joinedDate}</td>
                          </tr>
                          {profile.last_login_at && (
                            <tr className="border-b border-border/60">
                              <td className="py-1 text-muted-foreground min-w-[120px] sm:w-48">Last Login</td>
                              <td className="py-1 break-words">
                                {new Date(profile.last_login_at).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td className="py-1 text-muted-foreground min-w-[120px] sm:w-48">Subscription</td>
                            <td className="py-1">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                <Badge variant={isActive ? "default" : "secondary"} className={isActive ? "bg-green-100 text-green-800 hover:bg-green-100 w-fit" : "w-fit"}>
                                  {isActive ? "Active" : "Inactive"}
                                </Badge>
                                {isActive && profile.subscriptionEndDate && profile.role !== 'admin' && profile.role !== 'developer' && (
                                  <span className="text-sm text-muted-foreground break-words">
                                    until {(() => { const d = new Date(profile.subscriptionEndDate); return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}, ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; })()}
                                  </span>
                                )}
                                {isActive && (profile?.role === 'admin' || profile?.role === 'developer') && (
                                  <span className="text-sm text-green-600 break-words">Lifetime Access</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Community Section - hidden for now
          <Card className="overflow-hidden">
            <div className="flex flex-col sm:flex-row items-stretch">
              <div className="flex flex-col sm:w-48 flex-shrink-0 items-center justify-center gap-1 p-2">
                <img src="/assets/images/icon_comunity.png" alt="Community" className="h-16 w-16 flex-shrink-0" />
                <div className="text-center">
                  <h2 className="text-base font-bold leading-tight flex items-center justify-center gap-0.5">
                    Bandarmolo
                    <img src={logoUrl} alt="NY" className="h-4 inline-block" draggable={false} />
                  </h2>
                  <p className="text-xs text-muted-foreground">Communities</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 flex-1 min-w-0 p-2">
                <Card className="flex flex-col overflow-hidden border-t-4 border-t-[#0088cc] hover:shadow-md transition-shadow">
                  <CardHeader className="text-center pb-1 pt-3 flex-1">
                    <div className="mx-auto mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-[#0088cc]">
                      <Send className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-sm">Join Free Telegram Group</CardTitle>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Bandarmolony Forum</p>
                  </CardHeader>
                  <CardContent className="px-4 pb-3 pt-0">
                    <Button size="sm" className="w-full bg-[#0088cc] hover:bg-[#0088cc]/80 text-white" onClick={() => window.open(HOME_LINKS.telegramGroup, "_blank", "noopener,noreferrer")}>
                      Join Sekarang <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </CardContent>
                </Card>

                <Card className="relative flex flex-col overflow-hidden border-t-4 border-t-[#c28830] hover:shadow-md transition-shadow">
                  <div className="absolute right-[-35px] top-[30px] w-[140px] rotate-45 bg-[#c28830] py-0.5 text-center text-[10px] font-bold text-white tracking-wider z-10">
                    COMING SOON
                  </div>
                  <CardHeader className="text-center pb-1 pt-3 flex-1">
                    <div className="mx-auto mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-[#c28830]/15 overflow-hidden">
                      <img src="/assets/images/logo_profile.jpeg" alt="Trading" className="h-12 w-12 object-cover" />
                    </div>
                    <CardTitle className="text-sm">Join Free Trading Group</CardTitle>
                    <span className="text-[11px] text-muted-foreground">Buka Akun di Sekuritas Partner</span>
                  </CardHeader>
                  <CardContent className="px-3 pb-2 pt-0">
                    <Button size="sm" className="w-full bg-[#c28830] hover:bg-[#c28830]/80 text-white">
                      Buka Akun <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </CardContent>
                </Card>

                <Card className="relative flex flex-col overflow-hidden border-t-4 border-t-red-500 hover:shadow-md transition-shadow">
                  <div className="absolute right-[-35px] top-[30px] w-[140px] rotate-45 bg-red-500 py-0.5 text-center text-[10px] font-bold text-white tracking-wider z-10">
                    COMING SOON
                  </div>
                  <CardHeader className="text-center pb-1 pt-3 flex-1">
                    <div className="mx-auto mb-1 flex h-12 w-12 items-center justify-center rounded-full overflow-hidden">
                      <img src="/assets/images/laptop_profile.jpeg" alt="Bootcamp" className="h-14 w-14 object-cover rounded-full" />
                    </div>
                    <CardTitle className="text-sm">Join Trading Bootcamp</CardTitle>
                    <span className="text-[11px] text-muted-foreground">Bandarmolony</span>
                  </CardHeader>
                  <CardContent className="px-3 pb-2 pt-0">
                    <Button className="w-full bg-red-500 hover:bg-red-600 text-white" size="sm">
                      Lihat Kelas & Daftar <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </Card>
          */}

          <EditProfile isOpen={isEditProfileOpen} onClose={() => setIsEditProfileOpen(false)} profile={profile} onSave={handleUpdateProfile} />
          <EditPassword isOpen={isEditPasswordOpen} onClose={() => setIsEditPasswordOpen(false)} email={profile.email} onSubmit={handleChangePassword} />
        </div>
      </div>
    </div>
  );
}
